<template>
  <ValidationObserver v-slot="{ invalid, handleSubmit }" slim>
    <form @submit.prevent="handleSubmit(saveCustomer)" class="justify-content-between col-lg-12 py-3">
      <div class="form-row align-items-center py-3">
        <div v-if="newCustomerExists" class="alert alert-warning form-group col-lg-12" role="alert">
          Er bestaat al een inactieve klant met deze naam. Wilt u deze klant heractiveren?
          <div class="mt-2">
            <b-button class="btn-fill-pulse mr-2 btn-width" @click="reactivateCustomer()">Ja</b-button>
            <b-button class="btn-fill-pulse-dark btn-width" @click="toggleNewCustomerExists()">Nee</b-button>
          </div>
        </div>
        <TextInputField
            class="form-group col-lg-6" v-model="name"
            labelText="naam" required
            @input="seeIfCustomerAlreadyExists(name)"
            minLength="3" maxLength="40"/>
        <TextInputField
            class="form-group col-lg-6" v-model="abbreviation"
            labelText="afkorting" required
            minLength="3" maxLength="10"/>
        <TextInputField
            class="form-group col-lg-6" v-model="supervisorName"
            labelText="supervisor" required
            minLength="3" maxLength="40"/>
        <TextInputField
            class="form-group col-lg-6" v-model="supervisorEmail"
            labelText="email supervisor" required
            minLength="5" maxLength="120"/>
        <p class="col-lg-12 font-weight-bold">Starturen</p>
        <TimeInputField
            class="form-group col-md-4" v-model="morning"
            labelText="Dag" required/>
        <TimeInputField
            class="form-group col-md-4" v-model="evening"
            labelText="Avond" required/>
        <TimeInputField
            class="form-group col-md-4" v-model="night"
            labelText="Nacht" required/>
        <label class="col-lg-6 mt-4 mb-4">
          <select v-model="updatedParentCompany" @change="onParentSelection" class="form-control">
            <option value="" disabled>Selecteer een moederbedrijf</option>
            <option value="">Geen moederbedrijf</option>
            <option
                v-for="parentCompany in sortedCustomers"
                :key="parentCompany.id"
                :value="parentCompany">{{ parentCompany.name }}
            </option>
          </select>
        </label>
        <div class="custom-control col-lg-6 custom-switch">
          <input
              type="checkbox" class="custom-control-input"
              v-model="inheritFromParentCompany"
              id="inheritFromParent"/>
          <label class="custom-control-label" for="inheritFromParent">Facturatiegegevens van het moederbedrijf overnemen</label>
        </div>
        <TextInputField
            class="form-group col-lg-6" v-model="vat"
            labelText="BTW-nummer" required
            minLength="3" maxLength="40"/>
        <div class="form-group col-lg-6">
          <label>Betalingstermijn</label>
          <select v-model="paymentTerm" class="form-control">
            <option :value="null" disabled>Kies een betalingstermijn</option>
            <option v-for="dropdownElement in paymentTermOptions"
                    :key="dropdownElement.id"
                    :value="dropdownElement.value">{{ dropdownElement.value }} dagen
            </option>
          </select>
        </div>
        <p class="col-lg-12 font-weight-bold">Facturatieadres</p>
        <TextInputField
            class="form-group col-md-6" v-model="street"
            labelText="Straat" required/>
        <TextInputField
            class="form-group col-md-6" v-model="houseNumber"
            labelText="Huisnummer" required/>
        <TextInputField
            class="form-group col-md-6" v-model="postalCode"
            labelText="Postcode" required/>
        <TextInputField
            class="form-group col-md-6" v-model="city"
            labelText="Stad" required/>
        <p class="col-lg-12 font-weight-bold">Naam die op de factuur vermeld moet worden</p>
        <TextInputField
            class="form-group col-md-6" v-model="organisationName"
            labelText="Naam van de organisatie" required/>
        <TextInputField
            class="form-group col-md-6" v-model="financialSupervisorReference"
            labelText="Naam van de geadresseerde" required/>
        <div class="form-group col-md-6">
          <label>E-mail(s) voor de facturatie</label>
          <multiselect
              v-model="selectedEmails"
              :options="confirmedEmails"
              :multiple="true"
              :close-on-select="false"
              :clear-on-select="false"
              :preserve-search="true"
              placeholder="Selecteer één of meerdere"
              label="emailAddress"
              track-by="emailAddress"
              :preselect-first="true"
              :taggable="true"
              @tag="addTag">
          </multiselect>
        </div>
        <div class="custom-control col-md-6 custom-switch">
          <input
              type="checkbox" class="custom-control-input"
              v-model="wantsConcatenatedDocuments"
              id="wantsConcatenatedDocuments"/>
          <label class="custom-control-label" for="wantsConcatenatedDocuments">Factuur in één document</label>
        </div>
        <div class="custom-control col-md-6 custom-switch">
          <input
              type="checkbox" class="custom-control-input"
              v-model="invoiceToPeppol"
              id="invoiceToPeppol"/>
          <label class="custom-control-label" for="invoiceToPeppol">Stuur factuur naar PEPPOL</label>
        </div>
        <div v-if="invoiceToPeppol" class="form-group col-md-6">
          <label>Peppol nummertype</label>
          <select v-model="peppolNumberType" class="form-control">
            <option v-for="option in peppolNumberTypeOptions"
                    :key="option.id"
                    :value="option.value">{{ option.value }}
            </option>
          </select>
        </div>
        <TextInputField
            v-if="invoiceToPeppol"
            class="form-group col-md-6" v-model="peppolNumber"
            labelText="Peppol nummer" required
            minLength="3" maxLength="40"/>
        <p class="col-lg-6 font-weight-bold">Bonus tarieven (in %)</p>
        <div class="custom-control col-lg-6 custom-switch">
          <input
              type="checkbox" class="custom-control-input"
              @click="flipBonusRateMode()"
              :disabled="isDisabled()"
              v-model="isCustomBonusRates"
              id="isCustomBonusRates"/>
          <label class="custom-control-label" for="isCustomBonusRates">Aangepaste bonus tarieven gebruiken</label>
          <p style="color: #3b5a86" v-if="isImportBonusRateFromParent">Bonustarief moederbedrijf gebruiken</p>
        </div>
        <div v-if="isCustomBonusRates" class="col-md-12 form-row">
          <NumberInputField
              class="form-group col-md-6" v-model="lateBonus"
              labelText="late uren " required
              min-value="0" max-value="100"/>
          <NumberInputField
              class="form-group col-md-6" v-model="nightBonus"
              labelText="nacht uren" required
              min-value="0" max-value="100"/>
          <NumberInputField
              class="form-group col-md-6" v-model="saturdayBonus"
              labelText="zaterdagen" required
              min-value="0" max-value="100"/>
          <NumberInputField
              class="form-group col-md-6" v-model="holidayBonus"
              labelText="verlof" required
              min-value="0" max-value="100"/>
          <NumberInputField
              class="form-group col-md-6" v-model="interruptedBonus"
              labelText="onderbroken" required
              min-value="0" max-value="100"/>
          <NumberInputField
              class="form-group col-md-6" v-model="callout24Bonus"
              labelText="oproep 24" required
              min-value="0" max-value="100"/>
          <NumberInputField
              class="form-group col-md-6" v-model="callout48Bonus"
              labelText="oproep 48" required
              min-value="0" max-value="100"/>
        </div>
      </div>
      <div class="form-row align-items-center py-3">
        <div class="input-group col-6">
          <button
              type="submit" :disabled="invalid"
              class="btn-basic btn-fill-pulse">
            Opslaan
          </button>
        </div>
      </div>
    </form>
    <ImportParentCompanyModal
        :parentCompany="selectedParentCompanyDetail"
        @update-chosen-fields="updateChosenFieldsFromParent"
        @close="closeModal"/>
  </ValidationObserver>

</template>

<script>
import {mapActions, mapGetters, mapMutations} from "vuex";
import {ValidationObserver} from "vee-validate";
import TextInputField from "@/components/inputFields/TextInputField";
import TimeInputField from "@/components/inputFields/TimeInputField";
import {sortListByColumnAndDirection} from "@/util/SortingUtil";
import NumberInputField from "@/components/inputFields/NumberInputField.vue";
import Multiselect from "vue-multiselect";
import ImportParentCompanyModal from "@/components/modals/ImportParentCompanyModal.vue";
import {VoerroModal} from "@voerro/vue-modal";

export default {
  name: "CreateCustomerComponent",
  components: {
    Multiselect,
    NumberInputField,
    TimeInputField,
    TextInputField,
    ValidationObserver,
    ImportParentCompanyModal
  },
  data() {
    return {
      name: "",
      abbreviation: "",
      supervisorName: "",
      supervisorEmail: "",
      morning: "",
      evening: "",
      night: "",
      updatedParentCompany: "",
      vat: "",
      search: "",
      organisationName: "",
      financialSupervisorReference: "",
      street: "",
      houseNumber: "",
      postalCode: "",
      city: "",
      inheritFromParentCompany: true,
      newCustomerExists: false,
      paymentTerm: null,
      wantsConcatenatedDocuments: false,
      invoiceToPeppol: false,
      peppolNumberType: "VAT",
      peppolNumber: "",
      peppolNumberTypeOptions: [{"id": 1, "value": "VAT"}, {"id": 2, "value": "GNL"}],
      paymentTermOptions: [{"id": 1, "value": 30}, {"id": 2, "value": 60}, {"id": 3, "value": 90}],
      invoiceEmailAddress: "",
      isCustomBonusRates: false,
      lateBonus: 20,
      nightBonus: 35,
      saturdayBonus: 26,
      holidayBonus: 56,
      interruptedBonus: 50,
      callout24Bonus: 20,
      callout48Bonus: 10,
      selectedEmails: [],
      parentCompanyBonusRates: [],
      isImportBonusRateFromParent: false,
    };
  },
  computed: {
    ...mapGetters({
      customers: "administrationCustomer/customers",
      selectedCustomerDetail: "administrationCustomer/selectedCustomerDetail",
      selectedParentCompanyDetail: "administrationCustomer/selectedParentCompanyDetail",
      confirmedEmails: "administrationCustomer/confirmedEmails"
    }),
    sortedCustomers: function () {
      let sortedList = sortListByColumnAndDirection(this.customers, "id", true);
      return sortedList.filter(customer => {
        return customer.parentCompanyId === 0;
      }).filter(customer => {
        return customer.name.toLowerCase().includes(this.search.toLowerCase());
      });
    },
  },
  async created() {
    await this.fetchConfirmedEmailsList();
  },
  methods: {
    ...mapActions("administrationCustomer", ['createCustomer', 'fetchDetailsForParentCustomer', 'fetchCustomers', 'fetchDetailsForSelectedCustomer', 'toggleCustomerActive', 'updateBonusRateFromCustomer', 'fetchConfirmedEmailsList']),
    ...mapMutations({
      setSelectedCustomer: "administrationCustomer/setSelectedCustomer",
      setSelectedCustomerDetail: "administrationCustomer/setSelectedCustomerDetail",
    }),
    addTag(newTag) {
      const tag = {
        confirmationDateTime: null,
        confirmed: false,
        confirmer: null,
        emailAddress: newTag,
        token: null,
      };
      this.selectedEmails.push(tag);
      this.confirmedEmails.push(tag);
    },
    async saveCustomer() {
      const createCustomerDto = {
        name: this.name,
        abbreviation: this.abbreviation,
        supervisor: {
          name: this.supervisorName,
          email: this.supervisorEmail,
        },
        customerHours: {
          morning: this.morning,
          evening: this.evening,
          night: this.night,
        },
        invoiceAddress: {
          street: this.street,
          houseNumber: this.houseNumber,
          postalCode: this.postalCode,
          city: this.city
        },
        parentCompanyId: this.updatedParentCompany.id,
        vat: this.vat,
        invoiceAddressingDto: {
          organisationName: this.organisationName,
          financialSupervisorReference: this.financialSupervisorReference,
        },
        paymentTerm: this.paymentTerm == null ? 30 : this.paymentTerm,
        wantsConcatenatedDocuments: this.wantsConcatenatedDocuments,
        peppolEndpointDto: {
          active: this.invoiceToPeppol,
          type: this.peppolNumberType,
          number: this.peppolNumber
        },
        confirmedEmailDtos: this.selectedEmails,
        isCustomBonusRates: this.isImportBonusRateFromParent ? this.isImportBonusRateFromParent : this.isCustomBonusRates,
        bonusRatesList: this.isImportBonusRateFromParent ? this.parentCompanyBonusRates : [
          {bonusType: "LATE", percentage: this.lateBonus},
          {bonusType: "SATURDAY", percentage: this.saturdayBonus},
          {bonusType: "NIGHT", percentage: this.nightBonus},
          {bonusType: "HOLIDAY", percentage: this.holidayBonus},
          {bonusType: "CALLOUT24", percentage: this.callout24Bonus},
          {bonusType: "CALLOUT48", percentage: this.callout48Bonus},
          {bonusType: "INTERRUPTED", percentage: this.interruptedBonus}],
      };
      await this.createCustomer(createCustomerDto);
      this.resetFields();
      await this.fetchCustomers();
      this.$emit('customerAdded');
    },
    resetFields() {
      this.name = "";
      this.abbreviation = "";
      this.supervisorName = "";
      this.supervisorEmail = "";
      this.morning = "";
      this.evening = "";
      this.night = "";
      this.updatedParentCompany = "";
      this.vat = "";
      this.organisationName = "";
      this.financialSupervisorReference = "";
      this.street = "";
      this.houseNumber = "";
      this.postalCode = "";
      this.city = "";
      this.wantsConcatenatedDocuments = false;
      this.invoiceToPeppol = false;
      this.peppolNumberType = "VAT";
      this.peppolNumber = "";
      this.newCustomerExists = false;
      this.paymentTerm = null;
      this.invoiceEmailAddress = "";
    },
    async onParentSelection() {
      if (this.inheritFromParentCompany && this.updatedParentCompany !== "" && this.updatedParentCompany) {
        await this.fetchDetailsForParentCustomer(this.updatedParentCompany.id);
        await this.toggleModal();
      }
    },
    isDisabled() {
      if (this.isImportBonusRateFromParent) {
        this.isCustomBonusRates = false;
      }
      return this.isImportBonusRateFromParent;
    },
    toggleModal() {
      VoerroModal.show('importParentCompanyValues');
    },
    updateChosenFieldsFromParent(checkboxValues, parentCompany) {
      if (checkboxValues.vat) {
        this.vat = parentCompany.vat;
      }
      if (checkboxValues.invoiceAddress) {
        this.street = parentCompany.customerInvoiceAddress.street;
        this.houseNumber = parentCompany.customerInvoiceAddress.houseNumber;
        this.postalCode = parentCompany.customerInvoiceAddress.postalCode;
        this.city = parentCompany.customerInvoiceAddress.city;
      }
      if (checkboxValues.organisation) {
        this.organisationName = parentCompany.invoiceAddressingDto.organisationName;
        this.financialSupervisorReference = parentCompany.invoiceAddressingDto.financialSupervisorReference;
        this.selectedEmails = parentCompany.confirmedEmailDtos;
      }
      this.isImportBonusRateFromParent = checkboxValues.bonusRate;
      if (checkboxValues.bonusRate) {
        const parentBonusRateList = [];
        for (const bonusRateDto of parentCompany.bonusRateDtos) {
          parentBonusRateList.push(bonusRateDto);
        }
        this.parentCompanyBonusRates = parentBonusRateList;
      }
      if (checkboxValues.standardHours) {
        this.morning = parentCompany.customerHours.morning;
        this.evening = parentCompany.customerHours.evening;
        this.night = parentCompany.customerHours.night;
      }
      if (checkboxValues.paymentTerm) {
        this.paymentTerm = parentCompany.paymentTerm;
      }
      if (checkboxValues.supervisor) {
        this.supervisorName = parentCompany.supervisor.name;
        this.supervisorEmail = parentCompany.supervisor.email;
      }
    },
    closeModal() {
      VoerroModal.hide('importParentCompanyValues');
    },
    toggleNewCustomerExists() {
      this.newCustomerExists = !this.newCustomerExists;
    },
    seeIfCustomerAlreadyExists(enteredName) {
      this.newCustomerExists = this.customers.filter(function (customer) {
        return customer.name.toLowerCase() === enteredName.toLowerCase() && !customer.active;
      }).length >= 1;
    },
    async reactivateCustomer() {
      const enteredName = this.name;
      const customerToActivate = this.customers.filter(function (customer) {
        return customer.name.toLowerCase() === enteredName.toLowerCase();
      });
      this.setSelectedCustomerDetail(customerToActivate[0]);
      const updateActivationStatusDto = {
        newActivationStatus: true
      };
      await this.toggleCustomerActive(updateActivationStatusDto)
          .then(() => this.newCustomerExists = false);

      this.resetFields();
      await this.fetchCustomers();
      this.$emit('customerAdded');
    },
    flipBonusRateMode() {
      if (!this.isCustomBonusRates) {
        this.lateBonus = 20;
        this.nightBonus = 35;
        this.saturdayBonus = 26;
        this.holidayBonus = 56;
        this.interruptedBonus = 50;
        this.callout24Bonus = 20;
        this.callout48Bonus = 10;
      }
    },
  }
};
</script>

<style scoped>
.btn-width {
  width: 100px;
}
</style>
